/** Population summary and RBI Form C at a cut-off date (FR-RES-07). */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileDown, FileText } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { downloadFile, openFile } from '../../lib/files';
import { manilaToday } from '../../lib/queries';
import { Button, Card, FilterBar, PageHeader, Skeleton, TableWrap, Td, Th, filterClass } from '../../components/ui';

interface Summary {
  cutoff: string; households: number; inhabitants: number; bySex: { MALE: number; FEMALE: number };
  byPurok: { purok: string; households: number; male: number; female: number; total: number }[];
  byAgeBand: { band: string; male: number; female: number; total: number }[];
  bySector: { sector: string; label: string; total: number }[];
  movements: { from: string; to: string; registered: number; transferredIn: number; transferredOut: number; deceased: number };
  generatedBy: string; generatedAt: string;
}

export function RegistryReportsPage(): JSX.Element {
  const { can } = useAuth();
  const [cutoff, setCutoff] = useState(manilaToday());
  const [from, setFrom] = useState('');
  const qs = `cutoff=${cutoff}${from ? `&from=${from}` : ''}`;
  const s = useQuery({ queryKey: ['registry-summary', qs], queryFn: () => api<Summary>(`/registry/summary?${qs}`) });
  const [paper, setPaper] = useState('A4');
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Registry reports"
        description="Counts are as of the cut-off date: people registered by then and not yet moved out or deceased. Movements cover the period (default: the quarter)."
        actions={<>
          <Button variant="secondary" onClick={() => void openFile(`/registry/form-c.pdf?${qs}&paper=${paper}`).catch((e: Error) => window.alert(e.message))}><FileText aria-hidden="true" className="h-4 w-4" /> RBI Form C</Button>
          {can('residents.export') && <Button variant="secondary" onClick={() => void downloadFile(`/registry/summary.xlsx?cutoff=${cutoff}`).catch((e: Error) => window.alert(e.message))}><FileDown aria-hidden="true" className="h-4 w-4" /> Excel</Button>}
        </>}
      />
      <FilterBar>
        <label className="text-sm">Cut-off <input type="date" className={filterClass} value={cutoff} onChange={(e) => setCutoff(e.target.value)} /></label>
        <label className="text-sm">Movements from <input type="date" className={filterClass} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-sm">Paper <select className={filterClass} value={paper} onChange={(e) => setPaper(e.target.value)}><option>A4</option><option value="LETTER">Letter</option><option value="LEGAL">Legal</option></select></label>
      </FilterBar>
      {s.isLoading || !s.data ? <Skeleton className="h-96 w-full" /> : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-4">
            {[['Inhabitants', s.data.inhabitants], ['Households', s.data.households], ['Male', s.data.bySex.MALE], ['Female', s.data.bySex.FEMALE]].map(([k, v]) => (
              <Card key={k as string}><p className="text-sm text-slate-500">{k}</p><p className="text-2xl font-bold tabular-nums">{Number(v).toLocaleString()}</p></Card>
            ))}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <Card title="By purok / sitio">
              <TableWrap><thead><tr><Th>Purok</Th><Th>Households</Th><Th>Male</Th><Th>Female</Th><Th>Total</Th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">{s.data.byPurok.map((p) => <tr key={p.purok}><Td>{p.purok}</Td><Td className="tabular-nums">{p.households}</Td><Td className="tabular-nums">{p.male}</Td><Td className="tabular-nums">{p.female}</Td><Td className="tabular-nums font-medium">{p.total}</Td></tr>)}</tbody>
              </TableWrap>
            </Card>
            <Card title="Sectors">
              <TableWrap><thead><tr><Th>Sector</Th><Th>Persons</Th></tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">{s.data.bySector.map((x) => <tr key={x.sector}><Td>{x.label}</Td><Td className="tabular-nums">{x.total}</Td></tr>)}</tbody>
              </TableWrap>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">Movements {s.data.movements.from} – {s.data.movements.to}: {s.data.movements.registered} registered, {s.data.movements.transferredIn} moved in, {s.data.movements.transferredOut} moved out, {s.data.movements.deceased} deceased.</p>
            </Card>
          </div>
          <Card title="By age group">
            <TableWrap><thead><tr><Th>Age</Th><Th>Male</Th><Th>Female</Th><Th>Total</Th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">{s.data.byAgeBand.map((a) => <tr key={a.band}><Td>{a.band}</Td><Td className="tabular-nums">{a.male}</Td><Td className="tabular-nums">{a.female}</Td><Td className="tabular-nums font-medium">{a.total}</Td></tr>)}</tbody>
            </TableWrap>
          </Card>
          <p className="text-xs text-slate-500">Data cut-off {s.data.cutoff} · generated by {s.data.generatedBy}</p>
        </div>
      )}
    </div>
  );
}
